import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import toggleFiltersOpen from "@insite/client-framework/Store/Pages/SavedOrderList/Handlers/ToggleFiltersOpen";
import clearSearch from "@insite/client-framework/Store/Pages/SavedOrderList/Handlers/ClearSearch";
import {
    convertDateOnlyStringToDate,
    convertDateToDateOnlyString,
} from "@insite/client-framework/Common/Utilities/DateUtilities";
import updateSearchFields from "@insite/client-framework/Store/Pages/SavedOrderList/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Tag, { horizontalStyles, TagPresentationProps } from "@insite/mobius/Tag";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";

const mapStateToProps = (state: ApplicationState) => ({
    isFilterOpen: state.pages.savedOrderList.isFilterOpen,
    getCartsApiParameter: state.pages.savedOrderList.getCartsApiParameter,
});

const mapDispatchToProps = {
    updateSearchFields,
    clearSearch,
    toggleFiltersOpen,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SavedOrderListFilterStyles {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    heading?: TypographyPresentationProps;
    datesGridItem?: GridItemProps;
    datesContainer?: GridContainerProps;
    dateRangeFieldset?: InjectableCss;
    fromGridItem?: GridItemProps;
    fromDate?: DatePickerPresentationProps;
    toGridItem?: GridItemProps;
    toDate?: DatePickerPresentationProps;
    orderTotalGridItem?: GridItemProps;
    orderTotalContainer?: GridContainerProps;
    orderTotalFieldset?: InjectableCss;
    orderTotalOperatorGridItem?: GridItemProps;
    orderTotalOperatorSelect?: SelectPresentationProps;
    orderTotalAmountGridItem?: GridItemProps;
    orderTotalAmountTextField?: TextFieldPresentationProps;
    appliedFiltersContainer?: InjectableCss;
    appliedFilterTag?: TagPresentationProps;
    buttonsItem?: GridItemProps;
    clearFiltersButton?: ButtonPresentationProps;
    filterOverlay?: InjectableCss;
    headingText?: TypographyPresentationProps;
    closeContainer?: InjectableCss;
    customWrapper?: InjectableCss;
    clearButton?: ButtonPresentationProps;
    filterOrderButton?: ButtonPresentationProps;
}

export const savedOrderListFilterStyles: SavedOrderListFilterStyles = {
    container: {
        css: css`
            margin-bottom: 10px;
            width: 470px;
            border: 1px solid rgba(7,15,44,0.08);
            border-radius: 1px;
            background-color: #fff;
            position: absolute;
            z-index: 9;
            padding: 90px 35px 50px 35px;
            left: 282px;
            top: -160px;

            div[class^='GridOffset'], div[class*=' GridOffset'] {
                width: 100% !important;
                margin: 0 auto;
            }

            > div:last-child {
                > div {
                    padding: 0;
                }
            }
            > div > div {
                flex-direction: column;

                > div {
                    max-width: 100%;
                    flex-basis: 100%;

                    > div > div {
                        padding: 5px 0;
                    }
                }

                label {
                    color: #002554;
                    font-family: 'Roboto', sans-serif;
                    font-size: 15px;
                    font-weight: 500;
                    letter-spacing: 0.17px;
                    line-height: 30px;
                    display: inline-flex;
                    min-width: 90px;
                    max-width: 90px;
                    align-items: center;
                }

                label[data-test-selector="savedOrder_filterToDate-label"] {
                    min-width: 26px;
                    max-width: 26px;
                    margin-left: 0;
                }

                .react-datetime-picker__inputGroup__leadingZero {
                    margin: -1px 0px 0 3px
                }

                input:not(.react-datetime-picker__inputGroup__input), 
                select:not(.react-datetime-picker__inputGroup__input), 
                textarea, 
                .mobiusFileUpload, 
                .react-datetime-picker,
                input {
                    border-color: rgba(31,96,172,0.5) !important;
                    border-radius: 2px !important;
                    background-color: #fff !important;
                    color: #535E64 !important;
                }

                .react-datetime-picker {
                    padding-left: 5px !important;
                    min-width: 130px;
                }

                .react-datetime-picker__calendar-button {
                    svg {
                        height: 18px;
                        width: 18px;
                    }

                    &:focus {
                        span {
                            outline: none;
                        }
                    }
                }

                [class*="FormInputWrapper"] {
                    margin-top: 0 !important;
                }
               
            }

            @media screen and (min-width: 1280px) {
                &:before {
                    content: '';
                    display: inline-block;
                    position: absolute;
                    border-top: 30px solid transparent;
                    border-bottom: 30px solid transparent;
                    border-right: 30px solid #fff;
                    left: -25px;
                    top: 215px;
                }

                > div > div {
                    div[class^='GridOffset'], div[class*=' GridOffset'] {
                        flex-direction: row;
                        padding: 0 !important;
                    }

                    [class*="FormFieldStyle"] {
                        flex-direction: row;
                        padding: 0 !important;
                        align-items: center;
                    }
                }

                > div > div {
                    [class*="FormFieldStyle"] {
                        flex-direction: row;
                        padding: 0 !important;
                    }
                }

                .react-datetime-picker {
                    max-width: 127px;
                }

                div[data-test-selector="savedOrderList_fromDateFilter"],
                div[data-test-selector="savedOrderList_toDateFilter"] {
                    [class*="DescriptionWrapper"] {
                        display: none;
                    }
                }
            }

            @media screen and (min-width: 1280px) {
                > div > div {
                    flex-direction: row;
                    padding: 5px 0;

                    &:last-child {
                        flex-direction: column;
                        padding-top: 25px;
                    }
                }
            }

            @media screen and (max-width: 1279px) {
                z-index: 99;
                left: 0;
                top: -183px;
                width: 100%;
                padding: 70px 15px 50px 15px;

                > div > div > div[data-test-selector="savedOrderList_fromDateFilter"],
                > div > div > div[data-test-selector="savedOrderList_toDateFilter"] {
                    flex-direction: row;

                    [class*="FormInputWrapper"] {
                        padding-top: 0;
                        margin-bottom: 5px;
                    }
                }

                > div > div {
                    label[data-test-selector="savedOrder_filterToDate-label"] {
                        margin-left: 0;
                    }

                    [class*="FormInputWrapper"] {
                        margin-bottom: 10px;
                    }

                    [class*="FormFieldStyle"] {
                        padding: 5px 0 !important;
                    }
                }

                div[data-test-selector="savedOrderList_fromDateFilter"] > div {
                div[data-test-selector="savedOrderList_toDateFilter"] > div {
                    margin-right: 25px;
                    max-width: min-content;
                }
            }


            @media screen and (min-width: 1280px) {
                div[data-test-selector="savedOrderList_fromDateFilter"],
                div[data-test-selector="savedOrderList_toDateFilter"] {
                    [class*="DescriptionWrapper"] {
                        display: none;
                    }
                }
            }

            @media screen and (max-width: 767px) {
                .react-datetime-picker {
                    min-width: 100% !important;
                    max-width: 100%;
                }

                .react-datetime-picker__button {
                    position: absolute;
                    right: 10px;
                    top: 2px;
                }

                div[data-test-selector="savedOrderList_fromDateFilter"] {
                    width: 50%;
                    padding-right: 10px;

                    > div {
                        width: 100%;
                    }
                }

                div[data-test-selector="savedOrderList_toDateFilter"] {
                    width: 50%;
                    padding-left: 10px;

                    > div {
                        width: 100%;
                    }
                }

            }
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            padding-bottom: 0;
        `,
    },
    heading: {
        variant: "h5",
        css: css`
            margin: 0;
        `,
    },
    datesGridItem: {
        css: css`
            padding: 15px 30px;
        `,
        width: 12,
    },
    datesContainer: { 
        gap: 10,
        css: css`
            @media screen and (max-width: 767px) {
                > div {
                    justify-content: space-between;
                }
            }
        `,
    },
    dateRangeFieldset: {
        css: css`
            border: none;
            margin: 0;
            padding: 0;
            width: 100%;
        `,
    },
    filterOverlay: {
        css: css`
            position: absolute;
            z-index: 2;
            opacity: 0.32;
            background-color: #002554;
            top: -53px;
            left: 0;
            width: 100%;
            height: 104%;
        `,
    },
    headingText: { 
        variant: "h5",
        css: css`
            position: relative;
            font-size: 18px;
            font-weight: 500;
            &:after {
                height: 0.125rem;
                width: 2.5rem;
                background-color: rgb(198,53,39);
                content: "";
                display: inline-block;
                position: absolute;
                bottom: -0.25rem;
                left: 1px;
            }
        `,
    },
    closeContainer: {
        css: css`
           position: absolute;
           right: 2.25rem;
           top: 24px;
           cursor: pointer;
           .close-label {
                color: #333F48;
                font-family: Roboto;
                font-size: .875rem;
                letter-spacing: 0;
                line-height: 1;
                display: inline-block;
                margin-right: .625rem;
                vertical-align: middle;
           }
           .icon-close {
                display: inline-block;
                cursor: pointer;
                vertical-align: middle;
                line-height: 1;
                width: 1.125rem;
                height: 1.125rem;
                svg {
                    height: 1.125rem;
                    width: 1.125rem;
                    color: #868686;
                }
            }
            @media screen and (max-width: 1279px) {
                right: 1.25rem;
            }
        `,
    },
    fromGridItem: { 
        css: css`
            width: auto;
            max-width: initial;
            flex-basis: initial;

        `,
    },
    toGridItem: { 
        css: css`
            width: auto;
            max-width: initial;
            flex-basis: initial;
        `,
    },
    appliedFiltersContainer: { 
        css: css` 
            border-top: 1px solid #E4E8F0;
            margin-top: 15px;
            padding-top: 5px;

            .applied-filter-title {
                color: #333F48;
                font-family: 'Roboto',sans-serif;
                font-size: 14px;
                font-weight: 600;
                margin: 6px 10px 6px 0;
                display:inline-flex;
                max-width: 260px;
            }

            > div {
                background-color: #E5EEF7;
                border: 1px solid #2060AD;
                padding: 2px 0 2px 5px;
                min-height: 2px;
                border-radius: 0;
                font-size: 14px;
                line-height: 18px;
                display: inline-flex;
                margin: 6px 10px 6px 0;

                button {
                    background-color: #E5EEF7;
                    height: 18px;
                    position: relative;
                    top: -2px;
                    
                    &:hover,
                    &:focus,
                    &:active {
                        background: none;
                        border: none;
                    }

                    svg {
                        height: 18px;
                        width: 18px;
                    }
                }
            }

            @media screen and (max-width: 1279px) {
                margin-bottom: 10px;
            }
        `,
    },
    buttonsItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    clearFiltersButton: {
        variant: "secondary",
        css: css`
            margin-right: 10px;
        `,
    },
    toDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
    orderTotalGridItem: {
        css: css`
            padding: 15px 30px;
        `,
        width: 12,
    },
    orderTotalContainer: { gap: 10 },
    orderTotalFieldset: {
        css: css`
            border: none;
            margin: 0;
            padding: 0;
            width: 100%;
        `,
    },
    orderTotalOperatorGridItem: { width: 12 },
    orderTotalAmountGridItem: { width: 12 },
    orderTotalAmountTextField: {
        cssOverrides: {
            formInputWrapper: css`
                margin-top: 32px;
            `,
        },
    },
    fromDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
    clearButton: {
        variant: "secondary",
        css: css`
            color: #868686;
            font-family: 'Roboto', sans-serif;
            font-size: 15px;
            letter-spacing: 0;
            line-height: 21px;
            text-align: center;
            text-decoration: underline;
            display: inline-flex;
            justify-content: center;
            align-items: center;
            border: none;
            border: none !important;
            background: none !important;
            padding: 0;

            span {
                color: #868686 !important;
                font-weight: normal !important;
            }

            &:hover,
            &:active,
            &:focus {
                color: #868686 !important;
                border: none !important;
                background: none !important;
                padding: 0;

                span {
                    color: #868686 !important;
                    font-weight: normal !important;
                }
            }
        `,
    },
    filterOrderButton: {
        color: "primary",
        buttonType: "outline",
        css: css`
            margin-bottom: 20px;
            width: 100%;
            display: inline-flex;
            justify-content: center;
            align-items: center;
            text-transform: uppercase;
        `,
    },
    customWrapper: {
        css: css`
            margin-top: 25px;
            padding-top: 25px;
            border-top: 1px solid #D7D7D7;
            display: flex;
            flex-direction: column;
            width: 100%;
        `,
    },
};

const StyledDateRangeFieldSet = getStyledWrapper("fieldset");
const StyledOrderTotalFieldSet = getStyledWrapper("fieldset");

export const styles = savedOrderListFilterStyles;
let updateTimeoutId: any;
const SavedOrderListFilter = ({ isFilterOpen, getCartsApiParameter, updateSearchFields, clearSearch, toggleFiltersOpen}: Props) => {
    const [orderSubtotal, setOrderSubtotal] = React.useState("");
    const [orderSubtotalOperator, setOrderSubtotalOperator] = React.useState("");
    const [shipToValue, setValue] = React.useState("");
    const [setfromDate, setFDValue] = React.useState("");
    const [settoDate, setTDValue] = React.useState("");
    const orderTotalOperators = ["Greater Than", "Less Than", "Equal To"];


    const fromDate = getCartsApiParameter.fromDate ? new Date(getCartsApiParameter.fromDate) : undefined;
    const toDate = getCartsApiParameter.toDate ? new Date(getCartsApiParameter.toDate) : undefined;


    useEffect(() => {
        if (
            !orderSubtotal &&
            !orderSubtotalOperator &&
            getCartsApiParameter.orderSubtotal &&
            getCartsApiParameter.orderSubtotalOperator
        ) {
            setOrderSubtotal(getCartsApiParameter.orderSubtotal);
            setOrderSubtotalOperator(getCartsApiParameter.orderSubtotalOperator);
        }
    }, []);

    const orderSubtotalChangeHandler = (totalValue: string, totalOperator: string) => {
        if ((totalValue === "" && totalOperator !== "") || (totalValue !== "" && totalOperator === "")) {
            return;
        }
        setOrderSubtotal(totalValue);
        setOrderSubtotalOperator(totalOperator);
    };

    const fromDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        setFDValue(selectedDay ? selectedDay.toISOString() : "");
    };

    const clearFromDateChangeHandler = () => {
        updateSearchFields({
            fromDate: "",
        });
    };

    const clearToDateChangeHandler = () => {
        updateSearchFields({
            toDate: "",
        });
    };


    const toDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        setTDValue(selectedDay ? selectedDay.toISOString() : "")
    };

    const orderSubtotalValueChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const total = event.target.value;
        setOrderSubtotal(total);

        if (typeof updateTimeoutId === "number") {
            clearTimeout(updateTimeoutId);
        }

        updateTimeoutId = setTimeout(() => {
            orderSubtotalChangeHandler(total, orderSubtotalOperator);
        }, 350);
    };

    const orderSubtotalOperatorChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const operator = event.target.value;
        setOrderSubtotalOperator(operator);

        orderSubtotalChangeHandler(orderSubtotal, operator);
    };

    const resetOrderSubtotal = () => {
        setOrderSubtotal("");
        updateSearchFields({ orderSubtotalOperator: "", orderSubtotal: "" });
    };

    const clearFiltersClickHandler = () => {
        clearSearch();
        setOrderSubtotal("");
        setOrderSubtotalOperator("");
        resetOrderSubtotal();
        setTDValue("");
        setFDValue("");
        clearFromDateChangeHandler();
        clearToDateChangeHandler();
    };

    const setFilter = () => {
        const loadParameter = {
            ...getCartsApiParameter,
            fromDate: setfromDate || undefined,
            toDate: settoDate || undefined,
            orderSubtotal,
            orderSubtotalOperator: orderSubtotalOperator || undefined,
        };
        updateSearchFields(loadParameter);
        toggleFiltersOpen();
    };
    
    return (
        <>  
            {getCartsApiParameter.fromDate || getCartsApiParameter.toDate || getCartsApiParameter.orderSubtotal ? (
                <StyledWrapper {...styles.appliedFiltersContainer}>
                    <Typography as="span" className="applied-filter-title">Filters:</Typography>
                    {getCartsApiParameter.fromDate && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                clearFromDateChangeHandler();
                            }}
                        >
                            {`${translate("From")}: ${convertDateOnlyStringToDate(getCartsApiParameter.fromDate)?.toLocaleDateString()}`}
                        </Tag>
                    )}
                    {getCartsApiParameter.toDate && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                clearToDateChangeHandler();
                            }}
                        >
                            {`${translate("To")}: ${convertDateOnlyStringToDate(getCartsApiParameter.toDate)?.toLocaleDateString()}`}
                        </Tag>
                    )}
                    {getCartsApiParameter.orderSubtotalOperator && getCartsApiParameter.orderSubtotal && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                resetOrderSubtotal();
                            }}
                        >
                            {`${translate("Order Total")}: ${getCartsApiParameter.orderSubtotalOperator} ${
                                getCartsApiParameter.orderSubtotal
                            }`}
                        </Tag>
                    )}
                </StyledWrapper>
            ): ''}

            {!isFilterOpen &&
                <>            
                <GridContainer {...styles.container}>
                    <GridItem {...styles.headingGridItem}>
                        <Typography {...styles.headingText} as="h2">
                            {translate("Filter")}
                        </Typography>
                    </GridItem>
                    <Typography as="div" onClick={() => toggleFiltersOpen()} {...styles.closeContainer}>
                        <span className="close-label">Close filters</span>
                        <Icon className="icon-close" src="XCircle"/>
                    </Typography>
                    <GridItem {...styles.datesGridItem}>
                        <StyledDateRangeFieldSet {...styles.dateRangeFieldset}>
                            <GridContainer {...styles.datesContainer}>
                                <GridItem data-test-selector="savedOrderList_fromDateFilter" {...styles.fromGridItem}>
                                    <DatePicker
                                        {...styles.fromDate}
                                        data-test-selector="savedOrder_filterFromDate"
                                        label={translate("Date Range")}
                                        selectedDay={convertDateOnlyStringToDate(setfromDate)}
                                        onDayChange={fromDateChangeHandler}
                                        dateTimePickerProps={{
                                            clearIcon: null,
                                            maxDate: toDate,
                                            ...styles.fromDate?.dateTimePickerProps,
                                        }}
                                    />
                                </GridItem>
                                <GridItem data-test-selector="savedOrderList_toDateFilter" {...styles.toGridItem}>
                                    <DatePicker
                                        {...styles.toDate}
                                        data-test-selector="savedOrder_filterToDate"
                                        label={translate("To")}
                                        selectedDay={convertDateOnlyStringToDate(settoDate)}
                                        onDayChange={toDateChangeHandler}
                                        dateTimePickerProps={{
                                            clearIcon: null,
                                            minDate: fromDate,
                                            ...styles.toDate?.dateTimePickerProps,
                                        }}
                                    />
                                </GridItem>
                            </GridContainer>
                        </StyledDateRangeFieldSet>
                    </GridItem>
                    <GridItem {...styles.orderTotalGridItem}>
                        <StyledOrderTotalFieldSet {...styles.orderTotalFieldset}>
                            <VisuallyHidden as="label">{translate("Subtotal")}</VisuallyHidden>
                            <GridContainer {...styles.orderTotalContainer}>
                                <GridItem {...styles.orderTotalOperatorGridItem}>
                                    <Select
                                        {...styles.orderTotalOperatorSelect}
                                        label={translate("Subtotal")}
                                        value={orderSubtotalOperator}
                                        data-test-selector="savedOrderList_comparisonMethod"
                                        onChange={orderSubtotalOperatorChangeHandler}
                                    >
                                        <option value="">{translate("Select cart subtotal")}</option>
                                        <option key="Greater Than" value="Greater Than">
                                            {translate("Greater Than")}
                                        </option>
                                        <option key="Less Than" value="Less Than">
                                            {translate("Less Than")}
                                        </option>
                                        <option key="Equal To" value="Equal To">
                                            {translate("Equal To")}
                                        </option>
                                    </Select>
                                </GridItem>
                                <GridItem {...styles.orderTotalAmountGridItem}>
                                    {orderSubtotalOperator ? (
                                        <TextField
                                            {...styles.orderTotalAmountTextField}
                                            value={orderSubtotal}
                                            label="Amount"
                                            type="number"
                                            data-test-selector="savedOrderList_comparisonValue"
                                            onChange={orderSubtotalValueChangeHandler}
                                        />
                                    ): ""}
                                </GridItem>
                            </GridContainer>
                        </StyledOrderTotalFieldSet>
                    </GridItem>

                    <Typography as="div" {...styles.customWrapper}>
                        <Button
                            onClick={setFilter}
                            {...styles.filterOrderButton}>
                            {translate("Filter Saved Carts")}
                        </Button>
                        <Button
                            {...styles.clearButton}
                            onClick={clearFiltersClickHandler}
                        >
                            {translate("Clear Filters")}
                        </Button>
                    </Typography>

                </GridContainer>

                <Typography as="div" {...styles.filterOverlay} onClick={() => toggleFiltersOpen()} className="filter-overlay"></Typography>
                </>
            }
        </> 
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SavedOrderListFilter),
    definition: {
        group: "Saved Order List",
        displayName: "Search Results Filter",
        allowedContexts: ["SavedOrderListPage"],
        isSystem: true,
    },
};

export default widgetModule;
